import {
  useCallback, useEffect, useRef, useState, type JSX, type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar, SIDEBAR_ID } from './Sidebar';
import { TopBar } from './TopBar';

/** Matches the `max-width: 820px` breakpoint in responsive.css. */
export const MOBILE_NAV_QUERY = '(max-width: 820px)';

function useMediaQuery(query: string): boolean {
  const get = (): boolean =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(query).matches
      : false;
  const [matches, setMatches] = useState<boolean>(get);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(query);
    const onChange = (): void => setMatches(mql.matches);
    onChange();
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, [query]);
  return matches;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Staff shell: sidebar + main column.
 *
 * Wide screens: the sidebar is a normal grid column.
 * <= 820px: the sidebar is an off-canvas drawer. It is closed by default,
 * opened from the TopBar menu button, and closes on Escape, backdrop
 * click, its own close button, any navigation, or a resize back to wide.
 * While open, focus moves into it and Tab is trapped there; on close,
 * focus returns to the menu button.
 */
export function AppShell({ children }: { children: ReactNode }): JSX.Element {
  const isMobile = useMediaQuery(MOBILE_NAV_QUERY);
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const drawerOpen = open && isMobile;

  const close = useCallback((returnFocus = true) => {
    setOpen(false);
    if (returnFocus) menuBtnRef.current?.focus();
  }, []);

  // Route change closes the drawer.
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    setOpen(false);
  }, [loc.pathname, loc.search]);

  // Leaving the narrow layout closes it.
  useEffect(() => { if (!isMobile) setOpen(false); }, [isMobile]);

  // Focus in on open; lock page scroll while open.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const first = sidebarRef.current?.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [drawerOpen]);

  // Escape closes; Tab is trapped inside the drawer.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      const nodes = Array.from(sidebarRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      if (nodes.length === 0) return;
      const firstEl = nodes[0]!;
      const lastEl = nodes[nodes.length - 1]!;
      const active = document.activeElement;
      if (!sidebarRef.current?.contains(active)) { e.preventDefault(); firstEl.focus(); }
      else if (e.shiftKey && active === firstEl) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && active === lastEl) { e.preventDefault(); firstEl.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen, close]);

  return (
    <div className="app-shell">
      <Sidebar
        ref={sidebarRef}
        mobile={isMobile}
        open={drawerOpen}
        onClose={() => close()}
        onNavigate={() => setOpen(false)}
      />
      {drawerOpen && (
        <div
          className="sidebar-backdrop"
          data-testid="nav-backdrop"
          aria-hidden="true"
          onClick={() => close()}
        />
      )}
      <main>
        <TopBar
          menuButtonRef={menuBtnRef}
          navOpen={drawerOpen}
          navId={SIDEBAR_ID}
          onToggleNav={() => (drawerOpen ? close() : setOpen(true))}
        />
        {children}
      </main>
    </div>
  );
}
